/**
 * DATONAVIGASJONEN: `‹ 5. juli – 11. juli ›` SOM ÉN KAPSEL (Glass 2, DESIGN.md §16.5 punkt 6).
 *
 * Tre separate knapper — pil, tekst, pil — leste som tre ting. Det er én kontroll: perioden, og to
 * måter å flytte den på. Så sporet er tonalt og samlet, pilene er ikonknapper inne i det, og
 * teksten mellom dem er verdien.
 *
 * Det fantes fem kopier (verktøylinja, to månedsvelgere på spillestedene, artistkalenderen og
 * konsertkalenderen), med tre ulike pilformer og to uten `aria-live`. Denne er den ene.
 *
 * `aria-live` på teksten: en periode som endres med pilene endrer en tekst midt på skjermen, og uten
 * det er det den ENESTE tilbakemeldingen en skjermleserbruker ikke får — fokus står igjen på pila,
 * som sier det samme som før. Teksten reserverer sin egen bredde, ellers flytter «september 2026»
 * pila man nettopp traff.
 */
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { ICONS } from './icons';

export interface PeriodNavProps {
  /** Allerede formatert — kontrollen kjenner verken sone eller språk. */
  label: ReactNode;
  onBack: () => void;
  onForward: () => void;
  /** Navnet på pila — «Forrige uke», eller målperioden selv. Blir `aria-label` og tooltip. */
  backLabel: string;
  forwardLabel: string;
  backDisabled?: boolean;
  forwardDisabled?: boolean;
  /** `<testId>`, `<testId>-back`, `<testId>-label`, `<testId>-forward`. */
  testId?: string;
  /** Enkeltvise test-id-er der en side hadde sine egne navn fra før (de vinner over `testId`). */
  testIds?: Partial<Record<'root' | 'back' | 'label' | 'forward', string>>;
  /** `h2` når perioden er overskriften over et rutenett (en månedskalender). */
  labelAs?: 'span' | 'h2';
  /** Bredden teksten holder av. Standard 9rem — «14.–20. september 2026» får plass. */
  labelClassName?: string;
  className?: string;
}

const ARROW = cn(
  'hit-target inline-flex size-6 shrink-0 items-center justify-center rounded-full',
  'border border-transparent text-muted ring-focus',
  'transition-colors duration-[var(--duration-fast)] hover:bg-[var(--fill-tonal)] hover:text-default',
  'disabled:pointer-events-none disabled:opacity-40',
);

export function PeriodNav({
  label,
  onBack,
  onForward,
  backLabel,
  forwardLabel,
  backDisabled = false,
  forwardDisabled = false,
  testId,
  testIds,
  labelAs = 'span',
  labelClassName,
  className,
}: PeriodNavProps): ReactNode {
  const Back = ICONS.chevronLeft;
  const Forward = ICONS.chevronRight;
  const id = (part: 'back' | 'label' | 'forward'): Record<string, string> => {
    const named = testIds?.[part] ?? (testId === undefined ? undefined : `${testId}-${part}`);
    return named === undefined ? {} : { 'data-testid': named };
  };
  const rootId = testIds?.root ?? testId;
  const Label = labelAs;

  return (
    <div
      data-testid={rootId}
      className={cn(
        // `segmented-track` gir glass når kapselen ligger rett på bakgrunnen (globals.css).
        'segmented-track inline-flex h-7 shrink-0 items-center rounded-[var(--radius-control)] px-0.5',
        'bg-[var(--control-fill,var(--fill-tonal))]',
        className,
      )}
    >
      <button
        type="button"
        onClick={onBack}
        disabled={backDisabled}
        aria-label={backLabel}
        title={backLabel}
        className={ARROW}
        {...id('back')}
      >
        <Back aria-hidden className="size-3.5" />
      </button>
      <Label
        aria-live="polite"
        className={cn(
          'numeric min-w-36 px-1.5 text-center text-xs font-medium whitespace-nowrap text-default',
          labelClassName,
        )}
        {...id('label')}
      >
        {label}
      </Label>
      <button
        type="button"
        onClick={onForward}
        disabled={forwardDisabled}
        aria-label={forwardLabel}
        title={forwardLabel}
        className={ARROW}
        {...id('forward')}
      >
        <Forward aria-hidden className="size-3.5" />
      </button>
    </div>
  );
}
